import React, { useEffect, useRef, useState } from "react";
import { Icon } from "../shared/components";
import { maskBrazilPhoneInput, normalizeBrazilPhone } from "../shared/utils";

/* ── Field ──────────────────────────────────────── */
function Field({ label, hint, error, required, children, half }) {
  return (
    <div className={`cModal-field${half ? " cModal-field-half" : ""}`}>
      <label className="cModal-label">
        {label}
        {required && <span className="cModal-req">*</span>}
      </label>
      {children}
      {hint && !error && <div className="cModal-hint">{hint}</div>}
      {error && <div className="cModal-error">{error}</div>}
    </div>
  );
}

function FieldGroup({ children }) {
  return <div className="cModal-fieldGroup">{children}</div>;
}

function Section({ title, children }) {
  return (
    <div className="cModal-section">
      {title && <div className="cModal-sectionTitle">{title}</div>}
      <div className="cModal-sectionBody">{children}</div>
    </div>
  );
}

/* ── Tags input ─────────────────────────────────── */
function TagsInput({ value = [], onChange }) {
  const [input, setInput] = useState("");
  const inputRef = useRef(null);

  function add(v) {
    const t = v.trim().toLowerCase();
    if (!t || value.includes(t)) { setInput(""); return; }
    onChange?.([...value, t]);
    setInput("");
  }
  function remove(t) { onChange?.(value.filter((x) => x !== t)); }

  return (
    <div className="cModal-tagsInput" onClick={() => inputRef.current?.focus()}>
      {value.map((t) => (
        <span key={t} className="cModal-tagChip">
          {t}
          <button type="button" className="cModal-tagRemove" onClick={() => remove(t)} aria-label="Remover">
            <Icon name="close" size={9} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="cModal-tagsField"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(input); }
          if (e.key === "Backspace" && !input && value.length) remove(value[value.length - 1]);
        }}
        placeholder={value.length === 0 ? "cliente, vip, lead..." : ""}
      />
    </div>
  );
}

/* ── Main Modal ─────────────────────────────────── */
export default function ContactModal({ api, mode = "create", contact = null, onClose, onSaved }) {
  const isEdit = mode === "edit";

  const [name, setName] = useState(contact?.name || "");
  const [phone, setPhone] = useState(contact?.phone ? maskBrazilPhoneInput(contact.phone) : "");
  const [email, setEmail] = useState(contact?.email || "");
  const [company, setCompany] = useState(contact?.company || "");
  const [role, setRole] = useState(contact?.role || "");
  const [cpf, setCpf] = useState(contact?.cpf || "");
  const [tags, setTags] = useState(
    contact?.tags ? contact.tags.split(",").map((t) => t.trim()).filter(Boolean) : []
  );
  const [notes, setNotes] = useState(contact?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const valid = name.trim() && phone.trim();

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  async function handleSave() {
    setError("");
    const phoneDigits = normalizeBrazilPhone(phone);
    if (!name.trim()) return setError("Nome é obrigatório.");
    if (phoneDigits.length < 10) return setError("Telefone inválido. Informe DDD + número.");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("E-mail inválido.");

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        phone: phoneDigits,
        email: email.trim(),
        company: company.trim(),
        notes: notes.trim(),
        tags: tags.join(", "),
      };
      if (isEdit && contact?.id) {
        await api.put(`/contacts/${contact.id}`, payload);
      } else {
        await api.post("/contacts", payload);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Erro ao salvar contato.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="cModal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cModal-box cModal-lg" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="cModal-header">
          <div className="cModal-header-text">
            <div className="cModal-title">{isEdit ? "Editar contato" : "Novo contato"}</div>
            <div className="cModal-subtitle">
              {isEdit ? "Atualize as informações do cliente" : "Cadastre um cliente sem iniciar conversa"}
            </div>
          </div>
          <button type="button" className="cModal-close" onClick={onClose} aria-label="Fechar">
            <Icon name="close" size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="cModal-body">

          <Section title="Identificação">
            <FieldGroup>
              <Field label="Nome completo" required half>
                <input className="cModal-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: João da Silva" disabled={saving} />
              </Field>
              <Field label="CPF / CNPJ" half>
                <input className="cModal-input" value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="000.000.000-00" disabled={saving} />
              </Field>
            </FieldGroup>
          </Section>

          <Section title="Contato">
            <FieldGroup>
              <Field label="WhatsApp" required half>
                <div className="cModal-inputWrap">
                  <Icon name="whatsapp" size={13} style={{ color: "var(--brand-500)", flexShrink: 0 }} />
                  <input
                    className="cModal-input cModal-input-bare"
                    value={phone}
                    onChange={(e) => setPhone(maskBrazilPhoneInput(e.target.value))}
                    placeholder="(83) 99999-9999"
                    disabled={saving}
                  />
                </div>
              </Field>
              <Field label="E-mail" half>
                <div className="cModal-inputWrap">
                  <Icon name="mail" size={13} style={{ color: "var(--text-4)", flexShrink: 0 }} />
                  <input
                    className="cModal-input cModal-input-bare"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="cliente@empresa.com"
                    disabled={saving}
                  />
                </div>
              </Field>
            </FieldGroup>
          </Section>

          <Section title="Empresa">
            <FieldGroup>
              <Field label="Empresa" half>
                <input className="cModal-input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Nome da empresa" disabled={saving} />
              </Field>
              <Field label="Cargo" half>
                <input className="cModal-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Ex: Diretor comercial" disabled={saving} />
              </Field>
            </FieldGroup>
          </Section>

          <Section title="Categorização">
            <Field label="Tags" hint="Use vírgula ou Enter para adicionar · clique no × para remover">
              <TagsInput value={tags} onChange={setTags} />
            </Field>
            <Field label="Observações internas" hint="Visível apenas para a equipe">
              <textarea
                className="cModal-textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anotações sobre o cliente..."
                disabled={saving}
              />
            </Field>
          </Section>

          {error && (
            <div className="cModal-errorBar">
              <Icon name="warning" size={13} /> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="cModal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <div style={{ flex: 1 }} />
          {!isEdit && (
            <button type="button" className="btn btn-secondary" disabled={!valid || saving} onClick={async () => { await handleSave(); /* reopen empty */ }}>
              Salvar e adicionar outro
            </button>
          )}
          <button type="button" className="btn btn-primary" disabled={!valid || saving} onClick={handleSave}>
            <Icon name="check" size={13} />
            {saving ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar contato"}
          </button>
        </div>
      </div>
    </div>
  );
}
